/**
 * Binom-side 2-pane shell. Path CRUD in left column; lander/offer CRUD + drag
 * in right pane via context-supplied actions.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useEffect, useState } from 'react';

import type {
  BinomDraftItem,
  BinomDraftPath,
  BinomFlowDraft,
} from '../binom-flow-draft.types';
import type { PathLocator } from '../draft-structural';

import BinomPathsLeftColumn from './BinomPathsLeftColumn';
import BinomPathsRightPane from './BinomPathsRightPane';
import {
  defaultPathSelection,
  type BinomPathsSelection,
} from './binom-paths-selection';

export interface BinomPathsWorkspaceProps {
  draft: BinomFlowDraft;
  patchDefaultPath: (idx: number, m: (p: BinomDraftPath) => void) => void;
  patchGroupPath: (
    groupIdx: number,
    pathIdx: number,
    m: (p: BinomDraftPath) => void,
  ) => void;
  addDefaultPath: () => void;
  removeDefaultPath: (idx: number) => void;
  moveDefaultPath: (from: number, to: number) => void;
  addGroupPath: (groupIdx: number) => void;
  removeGroupPath: (groupIdx: number, pathIdx: number) => void;
  moveGroupPath: (groupIdx: number, from: number, to: number) => void;
  addLanderTo: (loc: PathLocator, item: BinomDraftItem) => void;
  removeLanderFrom: (loc: PathLocator, idx: number) => void;
  moveLanderIn: (loc: PathLocator, from: number, to: number) => void;
  addOfferTo: (loc: PathLocator, item: BinomDraftItem) => void;
  removeOfferFrom: (loc: PathLocator, idx: number) => void;
  moveOfferIn: (loc: PathLocator, from: number, to: number) => void;
}

const BinomPathsWorkspace: FC<BinomPathsWorkspaceProps> = (props) => {
  const {
    draft,
    patchDefaultPath,
    patchGroupPath,
    addDefaultPath,
    removeDefaultPath,
    moveDefaultPath,
    addGroupPath,
    removeGroupPath,
    moveGroupPath,
    addLanderTo,
    removeLanderFrom,
    moveLanderIn,
    addOfferTo,
    removeOfferFrom,
    moveOfferIn,
  } = props;
  const [selection, setSelection] = useState<BinomPathsSelection>(
    defaultPathSelection(0),
  );

  useEffect(() => {
    if (selection.kind === 'default-path') {
      if (selection.index >= draft.defaultPaths.length) {
        setSelection(defaultPathSelection(Math.max(0, draft.defaultPaths.length - 1)));
      }
    } else {
      const group = draft.pathGroups[selection.groupIndex];
      if (!group || selection.pathIndex >= group.paths.length) {
        setSelection(defaultPathSelection(0));
      }
    }
  }, [draft.defaultPaths.length, draft.pathGroups, selection]);

  const handleRemoveSelected = () => {
    if (selection.kind === 'default-path') removeDefaultPath(selection.index);
    else removeGroupPath(selection.groupIndex, selection.pathIndex);
  };

  return (
    <section className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border/80 bg-card p-2 shadow-none sm:p-2.5">
      <header className="shrink-0 space-y-2">
        <h3 className="text-xs font-semibold text-foreground">Paths</h3>
        <p className="text-[11px] leading-snug text-muted-foreground">
          Drag to reorder. + Path / + Lander / + Offer to add. Trash to remove.
        </p>
      </header>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden border-t border-border/60 pt-2 lg:flex-row lg:gap-0">
        <BinomPathsLeftColumn
          draft={draft}
          selection={selection}
          onSelect={setSelection}
          patchDefaultPath={patchDefaultPath}
          patchGroupPath={patchGroupPath}
          onAddDefaultPath={addDefaultPath}
          onMoveDefaultPath={moveDefaultPath}
          onAddGroupPath={addGroupPath}
          onMoveGroupPath={moveGroupPath}
        />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto border-border lg:border-l lg:pl-3">
          <BinomPathsRightPane
            selection={selection}
            draft={draft}
            patchDefaultPath={patchDefaultPath}
            patchGroupPath={patchGroupPath}
            onRemoveSelected={handleRemoveSelected}
            onAddLanders={(loc, items) => items.forEach((i) => addLanderTo(loc, i))}
            onRemoveLander={removeLanderFrom}
            onMoveLander={moveLanderIn}
            onAddOffers={(loc, items) => items.forEach((i) => addOfferTo(loc, i))}
            onRemoveOffer={removeOfferFrom}
            onMoveOffer={moveOfferIn}
          />
        </div>
      </div>
    </section>
  );
};

export default BinomPathsWorkspace;
